import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:shader')

/**
 * 着色器 Chunk 管理器
 * 对标 PlayCanvas ShaderChunks 系统，支持 #include 指令的递归解析
 *
 * 使用方式：
 * 1. 注册 chunks: manager.register('gsplatStructsVS', code)
 * 2. 组装着色器: manager.assemble('gsplatVS', { SH_BANDS: 2, GSPLAT_AA: '' })
 */
export class ShaderChunkManager {
  private static instance: ShaderChunkManager | null = null
  private chunks: Map<string, string> = new Map()

  private constructor() {}

  /**
   * 获取单例实例。
   *
   * @returns 全局唯一 ShaderChunkManager
   */
  static getInstance(): ShaderChunkManager {
    if (!ShaderChunkManager.instance) {
      ShaderChunkManager.instance = new ShaderChunkManager()
    }
    return ShaderChunkManager.instance
  }

  /**
   * 注册单个 chunk
   * @param name chunk 名称（如 'gsplatStructsVS'）
   * @param code GLSL 代码
   */
  register(name: string, code: string): void {
    this.chunks.set(name, code)
  }

  /**
   * 批量注册 chunks
   * @param chunks { name: code } 对象
   */
  registerAll(chunks: Record<string, string>): void {
    for (const [name, code] of Object.entries(chunks)) {
      this.register(name, code)
    }
  }

  /**
   * 获取 chunk 代码。
   *
   * @param name - chunk 名称
   * @returns 原始 GLSL 源码；未注册返回 undefined
   */
  get(name: string): string | undefined {
    return this.chunks.get(name)
  }

  /**
   * 处理 #include "..." 指令，递归解析所有依赖。
   *
   * 不做 include-once 去重：GLSL 没有前向声明，同名 chunk 可能出现在互斥的
   * #ifdef/#else 分支中，全局去重会导致非活跃分支的展开被跳过。
   *
   * @param source - 源代码
   * @param visited - 祖先路径集合，仅用于检测循环引用
   * @returns 展开所有 include 后的源码
   */
  processIncludes(
    source: string,
    visited: Set<string> = new Set(),
    chunkOverrides?: Readonly<Record<string, string | undefined>>
  ): string {
    const pattern = /#include\s+"([^"]+)"/g
    return source.replace(pattern, (_match, chunkName: string) => {
      // 防止循环引用
      if (visited.has(chunkName)) {
        log.warn(`[ShaderChunkManager] Circular include detected: ${chunkName}`)
        return `// [WARNING] Circular include skipped: ${chunkName}`
      }

      const hasOverride = chunkOverrides && Object.hasOwn(chunkOverrides, chunkName)
      const chunk = hasOverride ? chunkOverrides[chunkName] : this.chunks.get(chunkName)
      if (chunk === undefined) {
        log.warn(`[ShaderChunkManager] Chunk not found: ${chunkName}`)
        return `// [ERROR] Chunk not found: ${chunkName}`
      }

      // 回溯模式：标记 → 递归 → 移除，避免每次递归复制整个 Set
      visited.add(chunkName)
      const result = this.processIncludes(chunk, visited, chunkOverrides)
      visited.delete(chunkName)

      return result
    })
  }

  /**
   * 生成 #define 预处理指令
   * @param defines 定义对象
   */
  private generateDefines(defines: Record<string, string | number | boolean>): string {
    let header = ''
    for (const [key, value] of Object.entries(defines)) {
      if (value === '' || value === true) {
        // 无值定义：#define KEY
        header += `#define ${key}\n`
      } else if (value === false) {
        // 跳过 false 值
      } else {
        // 带值定义：#define KEY VALUE
        header += `#define ${key} ${value}\n`
      }
    }
    return header
  }

  /**
   * 组装完整着色器
   * @param entryChunk 入口 chunk 名称
   * @param defines 预处理定义（可选）
   * @returns 完整的着色器代码
   */
  assemble(
    entryChunk: string,
    defines?: Record<string, string | number | boolean>,
    chunkOverrides?: Readonly<Record<string, string | undefined>>
  ): string {
    const entry = this.chunks.get(entryChunk)
    if (!entry) {
      throw new Error(`[ShaderChunkManager] Entry chunk not found: ${entryChunk}`)
    }

    return this.assembleSource(entry, defines, chunkOverrides)
  }

  /**
   * 组装任意着色器源码
   * @param source 原始着色器源码
   * @param defines 预处理定义（可选）
   * @returns 完整的着色器代码
   */
  assembleSource(
    source: string,
    defines?: Record<string, string | number | boolean>,
    chunkOverrides?: Readonly<Record<string, string | undefined>>
  ): string {
    // 1. 生成 defines 头部
    let header = ''
    if (defines) {
      header = this.generateDefines(defines)
    }

    // 2. 处理 includes
    const processed = this.processIncludes(source, new Set(), chunkOverrides)

    return header + processed
  }
}

/**
 * 导出默认单例获取函数。
 *
 * @returns 全局唯一 ShaderChunkManager
 */
export function getShaderChunkManager(): ShaderChunkManager {
  return ShaderChunkManager.getInstance()
}
